import { zodParse } from 'base/zod_parse';
import { comparer, makeObservable, observable } from 'mobx';
import { z } from 'zod';
import { Connection, Domain, MutableOf } from './base';
import { MIN_TIMESAMP } from './clock';
import { AtomDelta, DeltaType } from './deltas';

// Only json serializable values are allowed
type AnyAtomValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | AnyAtomValue[]
  | { [key: string]: AnyAtomValue };

type AtomConfig<T extends AnyAtomValue> = {
  mutableBase: { set(v: T): void };
  state: Readonly<T>;
  delta: AtomDelta<T>;
  getter: { returns: Readonly<T> };
};

/**
 * Atom
 *
 * A domain containing a value which can be replaced.
 **/
export class AtomDomain<T extends AnyAtomValue>
  implements Domain<AtomConfig<T>>
{
  private readonly domainSymbol = Symbol('atom');

  constructor(readonly stateSchema: z.ZodType<T, any, any>) {}

  create(...args: Parameters<Domain<AtomConfig<T>>['create']>) {
    return new AtomMutable(...args, this.domainSymbol, this.stateSchema);
  }

  getter(m: MutableOf<AtomConfig<T>>) {
    return m.state;
  }

  setter(m: MutableOf<AtomConfig<T>>, v: T) {
    m.set(v);
  }

  statesEqual(a: T, b: T) {
    return comparer.structural(a, b);
  }

  isMutable(m: unknown): m is MutableOf<AtomConfig<T>> {
    return m instanceof AtomMutable && m.domainSymbol === this.domainSymbol;
  }
}

class AtomMutable<T extends AnyAtomValue> implements MutableOf<AtomConfig<T>> {
  readonly mutable = true;
  readonly visibleOids = [this.oid];

  private stateTimestamp = MIN_TIMESAMP;

  constructor(
    readonly oid: string,
    public state: T,
    private readonly cxn: Connection,
    readonly domainSymbol: symbol,
    readonly stateSchema: z.ZodType<T>,
  ) {
    makeObservable(this, {
      state: observable.ref,
    });
    cxn.register(this, (d) => {
      if (d.type !== DeltaType.ATOM_REPLACE) {
        throw new Error(`Unexpected delta for atom: ${d.type}`);
      }
      if (d.time > this.stateTimestamp) {
        // Assume the delta uses the right state format
        this.state = zodParse(this.stateSchema, d.to);
        this.stateTimestamp = d.time;
      }
    });
  }

  set(v: T) {
    if (v === this.state) return;
    this.cxn.fromMutable({
      for: this.oid,
      time: this.cxn.clock.advance(),
      type: DeltaType.ATOM_REPLACE,
      from: this.state,
      to: v,
    });
  }

  wouldChange(delta: AtomDelta<T>) {
    return (
      delta.type === DeltaType.ATOM_REPLACE && delta.time > this.stateTimestamp
    );
  }
}
